import React, { useState, useEffect } from "react";
import axios from "../../utils/axios";
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";
import { getUser } from "../../utils/Common";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Editor } from '@tinymce/tinymce-react';
import "react-toastify/dist/ReactToastify.css";

const PMSIntegration = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [isMiniSidebar, setMiniSidebar] = useState(false);
  const { hotel_id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [propData, setPropData] = useState({});
  const [formData, setFormData] = useState({});
  const [logoFile, setLogoFile] = useState(null); // State for handling logo upload
  const [logoPreview, setLogoPreview] = useState(null); // State for previewing the logo

  // Fetch hotel data
  const getDetailProperty = async (hotel_id) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/detail/hotel/${hotel_id}`);
      if (response.status === 200) {
        setPropData(response.data);
        setFormData(response.data); // Populate form with fetched data
      } else {
        throw new Error("Failed to fetch hotel details.");
      }
    } catch (err) {
      console.error("Error fetching property details:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle file change for logo upload with preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file)); // Generate a temporary URL for preview
    }
  };

  // Handle form submission for other fields
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      const response = await axios.post(`/api/update/hotel/setting`, formDataToSend);
      if (response.status === 200) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error updating hotel configuration:", err);
      toast.error("An error occurred. Please try again.");
    }
  };

  // Handle separate logo upload submission
  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("Please select a logo file before uploading.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("LOGO_HOTEL", logoFile);
    formDataToSend.append("ID_HOTEL", hotel_id); // Include the hotel ID for association

    try {
      const response = await axios.post(`/api/upload/hotel/logo`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        toast.success("Logo uploaded successfully!");
        console.log(response.data)
      } else {
        toast.error("Failed to upload logo.");
      }
    } catch (err) {
      console.error("Error uploading logo:", err);
      toast.error("An error occurred while uploading the logo.");
    }
  };

  useEffect(() => {
    const userSession = getUser(); // Call getUser to fetch user data from localStorage
        if (userSession) {
          setUserData(userSession); // Store user data in the state if it exists
      getDetailProperty(hotel_id);
    } else {
      navigate('/'); // Optionally clear the user data if not found
    }
  }, [hotel_id, navigate]);

  return (
    <div className="flex h-screen bg-gray-200">
      {/* Sidebar */}
      <Sidebar
        setMiniSidebar={setMiniSidebar}
        isMiniSidebar={isMiniSidebar}
        propertyID={propData.ID_HOTEL || "Loading..."}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar 
          setMiniSidebar={setMiniSidebar} 
          isMiniSidebar={isMiniSidebar} 
        />

        {/* Main Section */}
        <main className="flex-1 p-5 overflow-y-auto bg-gray-100">
          {/* Breadcrumb */}
          <div className="mb-4 text-sm text-[#101827]">
             <span>Integration Module PMS</span> &gt; <span>{propData.NAME_HOTEL}</span>
          </div>

          {/* Form Section */}
          <section className="bg-white rounded shadow-md py-4 px-6 text-xs text-[#101827]">
            

            {isLoading ? (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f1827] mb-4"></div>
                  <p className="text-gray-700">Load Data...</p>
                </div>
            </div>
            ) : (
              <>
                <h6 className="text-base font-semibold mb-4">Configuration</h6>

                {/* Main Configuration Form */}
                <form onSubmit={handleSubmit} className="flex flex-wrap gap-6">
                  {/* Full Width Fields */}
                  <div className="w-full">
                    {/* Right Column */}
                    <div className="flex-1 min-w-[300px]">
                    {[
                        { label: "ICNO", name: "ICNO", value: formData.ICNO },
                        { label: "ICKEY", name: "ICKEY", value: formData.ICKEY },
                        { label: "Segment ID", name: "SEGMENT_ID", value: formData.SEGMENT_ID },
                        { label: "Shared Key", name: "SHARED_KEY", value: formData.SHARED_KEY },
                        { label: "API Link", name: "API_LINK", value: formData.API_LINK },
                        { label: "API Port", name: "API_PORT", value: formData.API_PORT },
                        { label: "Business ID", name: "BUSINESS_ID", value: formData.BUSINESS_ID },
                        { label: "Client ID", name: "CLIENT_ID", value: formData.CLIENT_ID },
                        { label: "Company ID", name: "COMPANY_ID", value: formData.COMPANY_ID },
                    ].map((field, index) => (
                        <div key={index} className="mb-4">
                        <label className="block text-xs font-medium mb-1">{field.label}</label>
                        <input
                            type="text"
                            name={field.name}
                            value={field.value || ""}
                            onChange={handleChange}
                            className="w-full border rounded px-3 py-2"
                            required
                            disabled
                        />
                        </div>
                    ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="w-full text-right">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
                    >
                      Update Setting
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default PMSIntegration;
